import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Gift, Heart, DollarSign, MessageSquare, ArrowLeft, Tag, PoundSterling, Euro, AlertCircle, CreditCard } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SaveUserButton } from "@/components/SaveUserButton";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";
import StripePaymentForm from "@/components/StripePaymentForm";
import { isNativePaymentAvailable, presentPaymentSheet, initializeStripeNative } from "@/lib/stripeNative";

const GiftFlow = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("user") || "1";
  
  const [amount, setAmount] = useState("10");
  const [purpose, setPurpose] = useState<string>("");
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [recipient, setRecipient] = useState<{
    id: string;
    name: string;
    avatar_url?: string;
    status?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentStep, setPaymentStep] = useState<'details' | 'payment'>('details');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [awaitingDestination, setAwaitingDestination] = useState<boolean>(false);
  const [isNativePaymentProcessing, setIsNativePaymentProcessing] = useState(false);

  // Initialize native Stripe on mount
  useEffect(() => {
    initializeStripeNative();
  }, []);

  // Detect currency based on locale
  const { currency, currencySymbol, CurrencyIcon } = useMemo(() => {
    const locale = navigator.language || 'en-GB';
    const region = locale.split('-')[1] || 'GB';
    
    if (region === 'GB') {
      return { currency: 'gbp', currencySymbol: '£', CurrencyIcon: PoundSterling };
    } else if (['AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'].includes(region)) {
      return { currency: 'eur', currencySymbol: '€', CurrencyIcon: Euro };
    } else {
      return { currency: 'usd', currencySymbol: '$', CurrencyIcon: DollarSign };
    }
  }, []);

  useEffect(() => {
    const fetchRecipient = async () => {
      if (!userId) return;
      
      // Check if user is trying to send gift to themselves
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.id === userId) {
        toast({
          title: "Invalid recipient",
          description: "You cannot send a gift to yourself",
          variant: "destructive"
        });
        navigate("/map");
        return;
      }
      
      try {
        // Use safe_users view (excludes sensitive data like email, stripe_account_id)
        const { data, error } = await supabase
          .from('safe_users')
          .select('id, name, avatar_url, status')
          .eq('id', userId)
          .single();
        
        if (error) {
          console.error('Error fetching recipient:', error);
          toast({
            title: "Error",
            description: "Could not load recipient information",
            variant: "destructive"
          });
          return;
        }
        
        setRecipient(data);
      } catch (e) {
        console.error('Failed to fetch recipient:', e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecipient();
  }, [userId, toast, navigate]);

  const handleSendGift = async () => {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast({ 
        title: "Error", 
        description: "You must be logged in to send gifts", 
        variant: "destructive" 
      });
      return;
    }

    // Check if trying to send gift to self
    if (user.id === recipient?.id) {
      toast({ 
        title: "Invalid recipient", 
        description: "You cannot send a gift to yourself", 
        variant: "destructive" 
      });
      navigate("/map");
      return;
    }

    // Client-side validation
    const schema = z.object({
      amount: z.string()
        .transform((s) => parseFloat(s))
        .refine((n) => !Number.isNaN(n) && n > 0, { message: "Amount must be greater than 0" })
        .refine((n) => n <= 50, { message: "Maximum gift amount is £50" }),
      purpose: z.string().min(1, { message: "Please select a gift purpose" }),
      message: z.string().min(1, { message: "Please write a personal message with your gift." }).max(200),
    });

    const result = schema.safeParse({ amount, purpose, message });
    if (!result.success) {
      const first = result.error.errors[0];
      toast({ title: "Oops!", description: first.message, variant: "destructive" });
      return;
    }

    if (!recipient?.id) {
      toast({ 
        title: "Error", 
        description: "Recipient information not loaded", 
        variant: "destructive" 
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      // Call secure edge function with server-side validation
      const { data, error } = await supabase.functions.invoke('create-gift', {
        body: {
          recipient_id: recipient.id,
          amount: parseFloat(amount),
          purpose: purpose,
          message: message.trim(),
          is_anonymous: false
        }
      });

      if (error) {
        console.error('Error creating gift:', error);
        const status = (error as any)?.status ?? (error as any)?.context?.status ?? (error as any)?.cause?.status;
        const msg = (error as any)?.message || '';
        const isRateLimited = status === 429 || /rate[_\s-]?limit/i.test(msg) || /too many/i.test(msg);
        if (isRateLimited) {
          const retryMinutes = 15;
          throw new Error(`Rate limit exceeded. You can send another gift in ${retryMinutes} minute${retryMinutes > 1 ? 's' : ''}.`);
        }
        throw new Error(msg || 'Failed to send gift');
      }

      if (data?.error) {
        // Check for rate limit error
        if (data.rateLimited) {
          const retryMinutes = data.retryAfterMinutes || 15;
          throw new Error(`Rate limit exceeded. You can send another gift in ${retryMinutes} minute${retryMinutes > 1 ? 's' : ''}.`);
        }
        throw new Error(data.error);
      }

      console.log('Gift created successfully:', data);
      
      // Move to payment step with client secret
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setAwaitingDestination(!!data.awaitingDestination);
        setPaymentStep('payment');
      } else {
        throw new Error('No client secret returned from server');
      }
    } catch (error: any) {
      console.error('Failed to send gift:', error);
      
      toast({
        title: /rate[_\s-]?limit/i.test(error?.message || '') ? "Rate limit reached" : "Failed to send gift",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate fee: 10% with min £0.50, max £3.50
  const calculateFee = (giftAmount: number): number => {
    const percentage = giftAmount * 0.10;
    return Math.max(0.50, Math.min(percentage, 3.50));
  };

  const parsedAmount = parseFloat(amount) || 0;
  const fee = calculateFee(parsedAmount);
  const total = parsedAmount + fee;

  const quickAmounts = ["5", "10", "20", "50"];

  const giftPurposes = [
    { value: "tip", label: "💰 Tip", description: "For great service" },
    { value: "street_performance", label: "🎭 Street Performance", description: "For performers & artists" },
    { value: "busking", label: "🎸 Busking", description: "For street musicians" },
    { value: "kindness", label: "💝 Random Kindness", description: "Spread joy" },
    { value: "help", label: "🤝 Help Someone", description: "Lend a hand" },
    { value: "appreciation", label: "🙏 Appreciation", description: "Show gratitude" },
    { value: "celebration", label: "🎉 Celebration", description: "Celebrate something special" },
    { value: "other", label: "✨ Other", description: "Something else" },
  ];

  // Handle native payment (Google Pay / Apple Pay)
  const handleNativePayment = async () => {
    if (!clientSecret) return;
    
    setIsNativePaymentProcessing(true);
    try {
      const result = await presentPaymentSheet(clientSecret);
      if (result.success) {
        navigate("/thank-you");
      } else if (result.error && result.error !== 'Payment cancelled') {
        toast({ 
          title: "Payment failed", 
          description: result.error, 
          variant: "destructive" 
        });
      }
    } catch (error: any) {
      toast({ 
        title: "Payment failed", 
        description: error.message || "An unexpected error occurred", 
        variant: "destructive" 
      });
    } finally {
      setIsNativePaymentProcessing(false);
    }
  };

  // Show payment form if we have a client secret
  if (paymentStep === 'payment' && clientSecret) {
    const useNativePayment = isNativePaymentAvailable();
    
    return (
      <div className="min-h-screen bg-gradient-warm pb-safe">
        <div className="container mx-auto px-4 py-6 pb-8 max-w-md md:max-w-2xl lg:max-w-4xl">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => {
                setPaymentStep('details');
                setClientSecret('');
              }}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Gift Details
            </Button>
            <h1 className="text-2xl font-bold text-center bg-gradient-sunset bg-clip-text text-transparent">
              Complete Payment
            </h1>
          </div>

          <Card className="mb-4 shadow-warm border-border/50">
            <CardHeader>
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12 border-2 border-white shadow-soft">
                  <AvatarImage src={recipient?.avatar_url || undefined} />
                  <AvatarFallback>
                    {recipient?.name ? recipient.name[0].toUpperCase() : '?'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg">{currencySymbol}{amount} to {recipient?.name}</CardTitle>
                  <CardDescription className="text-sm line-clamp-1">{message}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          {awaitingDestination && (
            <Card className="mb-4 border-amber-300/60 bg-amber-50 text-amber-900">
              <CardContent className="pt-6 text-sm">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <div>
                    This recipient hasn't finished setting up payouts yet. We'll hold your gift securely and transfer it to them once they complete setup.
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="shadow-soft border-border/50">
            <CardContent className="pt-6">
              {useNativePayment ? (
                // Native payment sheet (Google Pay / Apple Pay)
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Pay securely using Google Pay, Apple Pay, or card
                  </p>
                  <Button
                    onClick={handleNativePayment}
                    disabled={isNativePaymentProcessing}
                    className="w-full"
                    size="lg"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {isNativePaymentProcessing ? "Processing..." : `Pay ${currencySymbol}${total.toFixed(2)}`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPaymentStep('details');
                      setClientSecret('');
                    }}
                    disabled={isNativePaymentProcessing}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                // Web payment form (fallback)
                <Elements 
                  stripe={stripePromise} 
                  options={{ 
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#F97316',
                      },
                    },
                  }}
                >
                  <StripePaymentForm
                    amount={total}
                    onSuccess={() => navigate("/thank-you")}
                    onCancel={() => {
                      setPaymentStep('details');
                      setClientSecret('');
                    }}
                  />
                </Elements>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm pb-safe">
      <div className="container mx-auto px-4 py-6 pb-8 max-w-md md:max-w-2xl lg:max-w-4xl">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold bg-gradient-sunset bg-clip-text text-transparent">
            Send a Gift
          </h1>
        </div>

        {/* Recipient Card */}
        {loading ? (
          <Card className="mb-6 shadow-warm border-border/50">
            <CardHeader>
              <div className="text-center text-muted-foreground">Loading recipient...</div>
            </CardHeader>
          </Card>
        ) : recipient ? (
          <Card className="mb-6 shadow-warm border-border/50">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 blur-lg rounded-full" />
                  <Avatar className="relative w-16 h-16 border-2 border-white shadow-soft">
                    <AvatarImage src={recipient.avatar_url || undefined} />
                    <AvatarFallback>
                      {recipient.name ? recipient.name[0].toUpperCase() : '?'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div>
                  <CardTitle>Sending to {recipient.name}</CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <Heart className="w-3 h-3 text-secondary" />
                    Spread some joy nearby
                  </CardDescription>
                  {recipient.status && (
                    <p className="text-sm text-accent flex items-center gap-1 mt-1">
                      <MessageSquare className="w-3 h-3" />
                      {recipient.status}
                    </p>
                  )}
                </div>
                <SaveUserButton userId={recipient.id} userName={recipient.name} size="default" />
              </div>
            </CardHeader>
          </Card>
        ) : (
          <Card className="mb-6 shadow-warm border-border/50">
            <CardHeader>
              <div className="text-center text-muted-foreground">Recipient not found</div>
            </CardHeader>
          </Card>
        )}

        {/* Amount Selection */}
        <Card className="mb-6 shadow-soft border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CurrencyIcon className="w-5 h-5 text-primary" />
              Gift Amount
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {quickAmounts.map((value) => (
                <Button
                  key={value}
                  variant={amount === value ? "default" : "outline"}
                  className={amount === value ? "bg-primary shadow-soft" : "border-border/50"}
                  onClick={() => setAmount(value)}
                >
                  {currencySymbol}{value}
                </Button>
              ))}
            </div>
            <div className="relative">
              <CurrencyIcon className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-10 border-border/50"
                placeholder="Custom amount"
                min="1"
                max="50"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Maximum gift amount: £50
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Gift Purpose */}
        <Card className="mb-6 shadow-soft border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-accent" />
              Gift Purpose
            </CardTitle>
            <CardDescription>
              What's this gift for?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger className="w-full border-border/50">
                <SelectValue placeholder="Select a purpose..." />
              </SelectTrigger>
              <SelectContent className="bg-background border-border/50 z-50">
                {giftPurposes.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    <div className="flex flex-col items-start py-1">
                      <span className="font-medium">{p.label}</span>
                      <span className="text-xs text-muted-foreground">{p.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Message */}
        <Card className="mb-6 shadow-soft border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-secondary" />
              Personal Message
            </CardTitle>
            <CardDescription>
              Add a kind note to brighten their day
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hey! Just wanted to spread some joy today..."
              className="min-h-[120px] border-border/50"
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {message.length}/200 characters
            </p>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="mb-6 shadow-soft border-border/50 bg-gradient-sunset/5">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gift Amount</span>
                <span className="font-medium">{currencySymbol}{parsedAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Platform Fee (10%, min {currencySymbol}0.50, max {currencySymbol}3.50)</span>
                <span className="font-medium">{currencySymbol}{fee.toFixed(2)}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span className="text-primary">{currencySymbol}{total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            className="w-full bg-primary hover:bg-primary/90 shadow-warm"
            size="lg"
            onClick={handleSendGift}
            disabled={isProcessing || loading || !recipient}
          >
            {isProcessing ? (
              "Processing..."
            ) : (
              <>
                <Gift className="w-5 h-5 mr-2" />
                Send {currencySymbol}{amount} Gift
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => navigate("/map")}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GiftFlow;